import React, { useState, useEffect, useCallback } from 'react';
import { nfConfirm } from './confirm';
import { api } from '../api';
import Modal from './Modal';
import { EditIcon, TrashIcon } from './ActionIcons';

// «Настройки → Точки перемещения»: свои магазины, куда передают товар со склада.
// Перемещение проводится актом в «Склад → Списания» с причиной «→ точка»: по себестоимости,
// без маржи и взаиморасчётов, в аналитике идёт отдельной строкой, а не потерями.
// Удаление = деактивация: на точку ссылаются проведённые акты.
// Сервер: /warehouse/transfer-points (backend/app/routers/warehouse.py).
function TransferPointsSettings({ showToast }) {
  const [points, setPoints] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', comment: '', is_active: true });

  const load = useCallback(async () => {
    try {
      const data = await api.getTransferPoints(showAll);
      data.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      setPoints(data);
    } catch {
      showToast('Ошибка загрузки точек', 'error');
    }
  }, [showAll, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', comment: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (point) => {
    setEditing(point);
    setForm({ name: point.name, comment: point.comment || '', is_active: point.is_active !== false });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showToast('Введите название точки', 'error');
      return;
    }
    try {
      if (editing) {
        await api.updateTransferPoint(editing.id, form);
        showToast('Точка обновлена');
      } else {
        await api.createTransferPoint(form);
        showToast('Точка создана');
      }
      setShowModal(false);
      load();
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!(await nfConfirm('Деактивировать точку? Она пропадёт из выбора при списании, проведённые акты останутся.', { confirmText: 'Деактивировать' }))) return;
    try {
      await api.deleteTransferPoint(id);
      showToast('Точка деактивирована');
      load();
    } catch (error) {
      showToast(error.message || 'Ошибка деактивации', 'error');
    }
  };

  return (
    <>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Ваши магазины, куда передаёте товар со склада. Перемещение проводится в «Склад → Списания»
        с причиной «→ точка»: по себестоимости, в аналитике — отдельной строкой, не потерями.
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Показать неактивные
        </label>
        <button className="btn btn-primary" onClick={openCreate}>
          + Добавить
        </button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Комментарий</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {points.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                  Точек пока нет
                </td>
              </tr>
            ) : points.map(point => (
              <tr key={point.id} style={{ opacity: point.is_active === false ? 0.5 : 1 }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{point.name}</td>
                <td style={{ padding: '8px 12px', color: point.comment ? '#333' : '#999' }}>
                  {point.comment || '—'}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span className={`badge badge-${point.is_active !== false ? 'active' : 'inactive'}`}>
                    {point.is_active !== false ? 'Активна' : 'Неактивна'}
                  </span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <div className="table-actions">
                    <button type="button" className="btn-icon" title="Изменить" aria-label="Изменить" onClick={() => openEdit(point)}>
                      <EditIcon />
                    </button>
                    {point.is_active !== false && (
                      <button type="button" className="btn-icon is-danger" title="Удалить" aria-label="Удалить" onClick={() => handleDelete(point.id)}>
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal
          title={editing ? 'Редактировать точку' : 'Новая точка перемещения'}
          onClose={() => setShowModal(false)}
          confirmOverlayClose
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Отмена</button>
              <button className="btn btn-primary" onClick={handleSubmit}>
                {editing ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Название</label>
            <input type="text" className="form-input" placeholder="Магазин на Ленина"
              value={form.name} autoFocus
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Комментарий</label>
            <textarea className="form-input" placeholder="Адрес, ответственный" rows={3}
              value={form.comment} style={{ resize: 'vertical' }}
              onChange={(e) => setForm(prev => ({ ...prev, comment: e.target.value }))} />
          </div>
          {editing && (
            <div className="form-group">
              <label className="form-checkbox">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))} />
                Активна (доступна для выбора при списании)
              </label>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

export default TransferPointsSettings;
